import React from 'react';
import {
    Box,
    Container,
    Typography,
    Link,
    Stack,
    IconButton,
    Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import GitHubIcon from '@mui/icons-material/GitHub';
import InstagramIcon from '@mui/icons-material/Instagram';
import White_Logo from '../assets/Logo/company_logo_white.svg';
import { useServices } from '../context/ServicesContext';

const Footer = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { services } = useServices();

    const socialIcons = [
        { icon: <LinkedInIcon />, link: '#' },
        { icon: <TwitterIcon />, link: '#' },
        { icon: <GitHubIcon />, link: '#' },
        { icon: <InstagramIcon />, link: '#' },
    ];

    const footerSections = t('layout.footer.sections', { returnObjects: true });

    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: theme.palette.background.footer,
                color: theme.palette.custom.footerTitle,
                pt: 10,
                pb: 4,
                mt: 'auto',
            }}
        >
            <Container maxWidth="xl">
                {/* ================= TOP SECTION ================= */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        gap: 6,
                        mb: 6,
                    }}
                >
                    {/* ================= BRAND ================= */}
                    <Box sx={{ flex: '1 1 220px', maxWidth: 720 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <img src={White_Logo} alt="NexusLink Logo" style={{ height: '40px' }} />
                        </Box>

                        <Typography
                            variant="h6"
                            sx={{
                                color: theme.palette.custom.footerText,
                                // lineHeight: 1.8,
                                maxWidth: 520,
                                mb: 4,
                            }}
                        >
                            {t('layout.footer.slogan')}
                        </Typography>

                        <Stack direction="row" spacing={1.5}>
                            {socialIcons.map((item, index) => (
                                <IconButton
                                    key={index}
                                    href={item.link}
                                    sx={{
                                        color: theme.palette.custom.footerIconColor,
                                        backgroundColor: theme.palette.custom.footerIconBg,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.custom.footerTitle,
                                        },
                                    }}
                                >
                                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                                </IconButton>
                            ))}
                        </Stack>
                    </Box>

                    {/* ================= LINKS ================= */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: { xs: 4, sm: 6, lg: 10 },
                            flex: '1 1 200px',
                        }}
                    >
                        {/* SERVICES */}
                        <Box sx={{ minWidth: 160 }}>
                            <Typography variant="subtitle1" fontWeight={500} mb={3}>
                                {footerSections.services.title}
                            </Typography>
                            <Stack spacing={2}>
                                {services.map((service, i) => (
                                    <Link
                                        key={service._id}
                                        href="#services"
                                        underline="none"
                                        color={theme.palette.custom.footerText}
                                        sx={{ '&:hover': { color: theme.palette.custom.footerLinkHover } }}
                                    >
                                        {service.title}
                                    </Link>
                                ))}
                            </Stack>
                        </Box>

                        {/* COMPANY */}
                        <Box sx={{ minWidth: 160 }}>
                            <Typography variant="subtitle1" fontWeight={500} mb={3}>
                                {footerSections.company.title}
                            </Typography>
                            <Stack spacing={2}>
                                {footerSections.company.links.map((link, i) => (
                                    <Link
                                        key={i}
                                        href={link === 'About Us' ? '#about' : '#'}
                                        underline="none"
                                        color={theme.palette.custom.footerText}
                                        sx={{ '&:hover': { color: theme.palette.custom.footerLinkHover } }}
                                    >
                                        {link}
                                    </Link>
                                ))}
                            </Stack>
                        </Box>

                        {/* SUPPORT */}
                        <Box sx={{ minWidth: 160 }}>
                            <Typography variant="subtitle1" fontWeight={500} mb={3}>
                                {footerSections.support.title}
                            </Typography>
                            <Stack spacing={2}>
                                {footerSections.support.links.map((link, i) => (
                                    <Link
                                        key={i}
                                        href={link === 'Contact' ? '#contact' : '#'}
                                        underline="none"
                                        color={theme.palette.custom.footerText}
                                        sx={{ '&:hover': { color: theme.palette.custom.footerLinkHover } }}
                                    >
                                        {link}
                                    </Link>
                                ))}
                            </Stack>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ borderColor: theme.palette.custom.footerDivider, mb: 3 }} />

                {/* ================= BOTTOM BAR ================= */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Typography variant="body2" color={theme.palette.custom.footerCopyright}>
                        {t('layout.footer.copyright')}
                    </Typography>

                    <Stack direction="row" spacing={3}>
                        {Object.values(t('layout.footer.legal_links', { returnObjects: true })).map(
                            (text, index) => (
                                <Link
                                    key={index}
                                    href="#"
                                    underline="none"
                                    color={theme.palette.custom.footerCopyright}
                                    fontSize="0.85rem"
                                    sx={{ '&:hover': { color: theme.palette.custom.footerLinkHover } }}
                                >
                                    {text}
                                </Link>
                            )
                        )}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;
